/**
 * Patient Dashboard Component
 * Displays patient's SOAP notes with filtering and viewing capabilities
 */

import { useState } from 'react';
import { FormattedText } from '../../components/common/FormattedText';
import { exportSOAPNoteToPDF } from '../../utils/soapExport';
import './PatientDashboard.css';

export const PatientDashboard = ({
  // User data
  currentUser,

  // Patient notes data
  patientNotes,
  loadingNotes,

  // Account claiming
  accountClaimed,
  claimingAccount,
  handleClaimAccount,

  // Filters
  patientFilterDateFrom,
  setPatientFilterDateFrom,
  patientFilterDateTo,
  setPatientFilterDateTo,

  // Modals
  showSuccessModal,
  setShowSuccessModal,
  successMessage,
  showErrorModal,
  setShowErrorModal,
  errorMessage,

  // Handlers
  handleLogout,
}) => {
  // Local state for UI
  const [activeNoteTabs, setActiveNoteTabs] = useState({});
  const [expandedPatientNote, setExpandedPatientNote] = useState(null);
  const [showPatientFilters, setShowPatientFilters] = useState(false);

  const userEmail = currentUser?.email;

  // Filter notes by date range
  const filteredPatientNotes = patientNotes.filter(note => {
    if (!patientFilterDateFrom && !patientFilterDateTo) return true;
    if (!note.appointment_date) return false;

    const noteDate = new Date(note.appointment_date);
    const fromDate = patientFilterDateFrom ? new Date(patientFilterDateFrom) : null;
    const toDate = patientFilterDateTo ? new Date(patientFilterDateTo) : null;

    if (fromDate && noteDate < fromDate) return false;
    if (toDate && noteDate > toDate) return false;

    return true;
  });

  // Helper function to strip markdown formatting
  const stripMarkdown = (raw = '') => raw
    .replace(/!\[[^\]]*]\([^)]+\)/g, '')
    .replace(/\[([^\]]+)]\([^)]+\)/g, '$1')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/`/g, '')
    .replace(/>\s*/gm, '')
    .replace(/\s+/g, ' ')
    .trim();

  return (
    <div className="App">
      <div className="container app-page-container">
        <div className="header-section">
          <div className="header-top">
            <div className="logo-wrapper">
              <div className="logo-icon">
                <svg viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Algodentra logo">
                  <g transform="translate(8.5 0)">
                    <path d="M10 30 L20 10 L30 30 Z" stroke="#ffffff" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="38" y1="20" x2="38" y2="20" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="43" y1="15" x2="43" y2="25" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="48" y1="10" x2="48" y2="30" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="53" y1="12" x2="53" y2="28" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="58" y1="18" x2="58" y2="22" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="63" y1="14" x2="63" y2="26" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="68" y1="16" x2="68" y2="24" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="73" y1="20" x2="73" y2="20" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                  </g>
                </svg>
              </div>
              <div className="mobile-lp-logo">
                <svg width="120" height="60" viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Algodentra logo">
                  <g>
                    <path d="M10 30 L20 10 L30 30 Z" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="38" y1="20" x2="38" y2="20" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="43" y1="15" x2="43" y2="25" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="48" y1="10" x2="48" y2="30" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="53" y1="12" x2="53" y2="28" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="58" y1="18" x2="58" y2="22" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="63" y1="14" x2="63" y2="26" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="68" y1="16" x2="68" y2="24" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="73" y1="20" x2="73" y2="20" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  </g>
                </svg>
              </div>
              <h1 className="title">Algodentra</h1>
            </div>

            <div className="header-actions">
              <span className="user-email">{userEmail}</span>
              <button className="pd-action-btn pd-danger" onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Patient Portal Content */}
        <div className="main-content patient-portal-layout">
          <div className="pd-hero patient-hero">
            <div className="pd-hero-left">
              <div className="pd-copy">
                <h2 className="pd-title">Review your secure SOAP notes</h2>
                <p className="pd-subtitle">
                  Access visit summaries, follow-up instructions, and keep your care aligned with your dentist.
                </p>
              </div>
            </div>
            <div className="pd-hero-right">
              <div className="pd-patient-chip">
                <span className="pd-chip-label">Records</span>
                <strong>{filteredPatientNotes.length ? `${filteredPatientNotes.length} available` : 'None yet'}</strong>
              </div>
            </div>
          </div>

          <div className="patient-records-shell">
            <div className="records-header-row">
              <div className="records-title-row">
                <div className="records-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <h3 className="records-heading">Your medical records</h3>
                  <p className="records-subheading">
                    {filteredPatientNotes.length} {filteredPatientNotes.length === 1 ? 'record' : 'records'} available
                  </p>
                </div>
              </div>
              <button
                className={`filter-toggle ${showPatientFilters ? 'active' : ''}`}
                onClick={() => setShowPatientFilters(!showPatientFilters)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 3H2L10 12.46V19L14 21V12.46L22 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {showPatientFilters ? 'Hide filters' : 'Filter by date'}
              </button>
            </div>

            {showPatientFilters && (
              <div className="records-filter-panel">
                <div className="filter-grid">
                  <div className="filter-field">
                    <label className="field-label">From</label>
                    <input
                      className="field-input"
                      type="date"
                      value={patientFilterDateFrom}
                      onChange={(e) => setPatientFilterDateFrom(e.target.value)}
                    />
                  </div>
                  <div className="filter-field">
                    <label className="field-label">To</label>
                    <input
                      className="field-input"
                      type="date"
                      value={patientFilterDateTo}
                      onChange={(e) => setPatientFilterDateTo(e.target.value)}
                    />
                  </div>
                </div>
                <div className="filter-actions">
                  <button className="pd-primary" onClick={() => setShowPatientFilters(false)}>Apply filters</button>
                  <button className="pd-ghost" onClick={() => { setPatientFilterDateFrom(''); setPatientFilterDateTo(''); }}>Reset</button>
                </div>
              </div>
            )}

            {loadingNotes ? (
              <div className="records-empty loading-state">
                <p>Loading your medical records...</p>
              </div>
            ) : filteredPatientNotes.length === 0 ? (
              <div className="records-empty">
                <p className="records-empty-title">No records yet</p>
                <p className="records-empty-copy">
                  Your SOAP notes from consultations will appear here after your doctor creates them.
                </p>
                {!accountClaimed && (
                  <div className="claim-card">
                    <p>If your doctor has already created a patient record for you, click below to link it:</p>
                    <button
                      className="pd-primary"
                      onClick={handleClaimAccount}
                      disabled={claimingAccount}
                    >
                      {claimingAccount ? 'Claiming account...' : 'Claim my medical records'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="patient-note-list">
                {filteredPatientNotes.map((note, idx) => {
                  const noteKey = note.id || note._id || idx;
                  const soapPayload = note.soapNote?.soap_note || note.soap_note || note.soapNote || note.soap_note?.soap_note || {};
                  const noteDetails = {
                    subjective: soapPayload.subjective || soapPayload.subjective_text || '',
                    objective: soapPayload.objective || soapPayload.objective_text || '',
                    assessment: soapPayload.assessment || soapPayload.assessment_text || '',
                    plan: soapPayload.plan || soapPayload.plan_text || ''
                  };
                  const hasSoapContent = Object.values(noteDetails).some(Boolean);
                  const overviewRaw = note.appointment_overview || note.soapNote?.appointment_overview || note.soap_note?.appointment_overview || '';
                  const overviewText = overviewRaw ? overviewRaw.replace(/(#+\s)/g, '\n$1') : '';
                  const hasOverview = !!(overviewText && overviewText.trim());
                  const isOpen = expandedPatientNote === noteKey;
                  const isAnotherOpen = expandedPatientNote && expandedPatientNote !== noteKey;
                  const activeTab = activeNoteTabs[noteKey] || (hasOverview ? 'overview' : 'soap');
                  const previewSource = overviewText || noteDetails.subjective || noteDetails.plan || '';
                  const previewPlain = stripMarkdown(previewSource);
                  const preview = previewPlain.slice(0, 140).trim();
                  const hasMoreContent =
                    previewPlain.length > 140 ||
                    (overviewText || noteDetails.subjective || noteDetails.plan || '').length > 140;
                  const formattedDate = note.created_at
                    ? new Date(note.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
                    : 'Date unknown';

                  return (
                    <div
                      key={noteKey}
                      className={`patient-note-card ${isOpen ? 'open' : ''} ${isAnotherOpen ? 'hidden-when-open' : ''}`}
                    >
                      <div className="note-summary" onClick={() => setExpandedPatientNote(isOpen ? null : noteKey)}>
                        <div className="note-summary-content">
                          <div className="note-summary-top">
                            <div className="note-summary-left">
                              <h4 className="note-title">{note.patient_name || 'Appointment'}</h4>
                              <p className="note-meta">{formattedDate}</p>
                              {note.doctor_name && (
                                <p className="note-doctor">
                                  Dr. {note.doctor_name}
                                  {note.license_number && ` • License #${note.license_number}`}
                                </p>
                              )}
                            </div>
                            <svg className="note-caret" width="20" height="20" viewBox="0 0 20 20" fill="none">
                              <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          {!isOpen && (
                            <>
                              <p className="note-preview">
                                {preview}{hasMoreContent ? '...' : ''}
                              </p>
                              <div className="note-tags">
                                {note.language && note.language !== 'en' && (
                                  <span className="note-chip note-chip-info">
                                    {note.language.toUpperCase()}
                                  </span>
                                )}
                                {note.pii_redacted && (
                                  <span className="note-chip note-chip-success">PII Redacted</span>
                                )}
                                {note.clinical_support && (
                                  <span className="note-chip note-chip-info">AI Support</span>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {isOpen && (
                        <div className="note-expand">
                          {hasOverview && hasSoapContent && (
                            <div className="note-tabs">
                              <button
                                className={`note-tab ${activeTab === 'overview' ? 'active' : ''}`}
                                onClick={() => setActiveNoteTabs(prev => ({ ...prev, [noteKey]: 'overview' }))}
                              >
                                Overview
                              </button>
                              <button
                                className={`note-tab ${activeTab === 'soap' ? 'active' : ''}`}
                                onClick={() => setActiveNoteTabs(prev => ({ ...prev, [noteKey]: 'soap' }))}
                              >
                                Full SOAP Note
                              </button>
                            </div>
                          )}

                          {activeTab === 'overview' && hasOverview && (
                            <div className="note-section">
                              <FormattedText text={overviewText} />
                            </div>
                          )}

                          {activeTab === 'soap' && hasSoapContent && (
                            <div className="note-section glass">
                              {noteDetails.subjective && (
                                <div className="note-soap">
                                  <div className="note-label">Subjective</div>
                                  <div className="note-line"><FormattedText text={noteDetails.subjective} /></div>
                                </div>
                              )}
                              {noteDetails.objective && (
                                <div className="note-soap">
                                  <div className="note-label">Objective</div>
                                  <div className="note-line"><FormattedText text={noteDetails.objective} /></div>
                                </div>
                              )}
                              {noteDetails.assessment && (
                                <div className="note-soap">
                                  <div className="note-label">Assessment</div>
                                  <div className="note-line"><FormattedText text={noteDetails.assessment} /></div>
                                </div>
                              )}
                              {noteDetails.plan && (
                                <div className="note-soap">
                                  <div className="note-label">Plan</div>
                                  <div className="note-line"><FormattedText text={noteDetails.plan} /></div>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="note-footer">
                            <button
                              className="pd-action-btn pd-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                exportSOAPNoteToPDF({
                                  patientName: note.patient_name,
                                  appointmentDate: note.created_at,
                                  fileName: formattedDate,
                                  soapNote: { soap_note: noteDetails },
                                  dentistName: note.doctor_name || note.dentist_name || '',
                                  licenseNumber: note.license_number || '',
                                });
                              }}
                            >
                              Export PDF
                            </button>
                            <button className="pd-action-btn pd-ghost" onClick={() => setExpandedPatientNote(null)}>Close</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
